<script setup lang="ts">
import BetaDnsRecordCard from '@/components/beta-dashboard/molecules/BetaDnsRecordCard.vue'
import notification from '@/services/notification'

const props = withDefaults(defineProps<{
    targetIp: string
    domain?: string
    checking?: boolean
    canVerify?: boolean
}>(), {
    domain: 'seudominio.com.br',
    checking: false,
    canVerify: false,
})

const emit = defineEmits<{
    verify: []
}>()

const copyValue = async (value: string) => {
    if (!value) return

    try {
        await navigator.clipboard.writeText(value)
        notification.simple('Destino copiado.', 'success')
    } catch {
        notification.simple('Não foi possível copiar. Selecione o valor manualmente.', 'error')
    }
}
</script>

<template>
    <section class="beta-dns-guide">
        <div class="beta-dns-guide__heading">
            <div>
                <p>Tutorial de DNS</p>
                <h3>Conecte seu domínio em três passos</h3>
                <span>As telas mudam entre provedores, mas os dois registros são sempre os mesmos.</span>
            </div>
            <small>Configuração única</small>
        </div>

        <ol class="beta-dns-guide__steps">
            <li>
                <span>1</span>
                <div>
                    <strong>Abra a zona DNS</strong>
                    <p>Entre no provedor onde registrou o domínio e procure por “DNS”, “Zona DNS” ou “Gerenciar DNS”.</p>
                </div>
            </li>
            <li>
                <span>2</span>
                <div>
                    <strong>Crie ou edite estes registros</strong>
                    <p>Se já existir um registro para <b>@</b> ou <b>www</b>, edite-o em vez de criar outro igual.</p>
                </div>
            </li>
        </ol>

        <div class="beta-dns-guide__records">
            <BetaDnsRecordCard
                type="A"
                host="@"
                :value="props.targetIp"
                description="Conecta o endereço principal"
                :disabled="!props.targetIp"
                @copy="copyValue"
            />
            <BetaDnsRecordCard
                type="CNAME"
                host="www"
                :value="props.domain"
                description="Conecta o endereço com www"
                @copy="copyValue"
            />
        </div>

        <div class="beta-dns-guide__ttl">
            <strong>TTL</strong>
            <span>Use o valor padrão do provedor ou 1 hora.</span>
        </div>

        <div class="beta-dns-guide__providers">
            <details>
                <summary>
                    <span>
                        <strong>Passo a passo no Registro.br</strong>
                        <small>Domínios terminados em .br</small>
                    </span>
                    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        <path d="m6 8 4 4 4-4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </summary>
                <div class="beta-dns-guide__provider-content">
                    <ol>
                        <li>Acesse <a href="https://registro.br" target="_blank" rel="noopener noreferrer">registro.br</a> e selecione seu domínio.</li>
                        <li>Abra <b>DNS</b> e escolha <b>Configurar endereçamento</b> ou <b>Editar zona</b>.</li>
                        <li>Entre no <b>Modo avançado</b>. Se ele acabou de ser ativado, aguarde alguns minutos.</li>
                        <li>Adicione o registro <b>A</b> com host vazio ou <b>@</b> e o IP mostrado acima.</li>
                        <li>Adicione o <b>CNAME</b> com host <b>www</b> apontando para <b>{{ props.domain }}</b>.</li>
                        <li>Salve as alterações.</li>
                    </ol>
                </div>
            </details>

            <details>
                <summary>
                    <span>
                        <strong>Passo a passo na GoDaddy</strong>
                        <small>Gerenciamento de DNS</small>
                    </span>
                    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        <path d="m6 8 4 4 4-4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </summary>
                <div class="beta-dns-guide__provider-content">
                    <ol>
                        <li>Abra <b>Meus produtos</b>, selecione o domínio e entre em <b>Gerenciar DNS</b>.</li>
                        <li>Adicione ou edite o registro <b>A</b>: host <b>@</b>, destino igual ao IP acima e TTL de 1 hora.</li>
                        <li>Adicione ou edite o <b>CNAME</b>: host <b>www</b>, destino <b>{{ props.domain }}</b> e TTL de 1 hora.</li>
                        <li>Salve as alterações.</li>
                    </ol>
                </div>
            </details>
        </div>

        <div class="beta-dns-guide__finish">
            <span>3</span>
            <div>
                <strong>Salve, aguarde a propagação e verifique</strong>
                <p>A atualização costuma aparecer em alguns minutos, mas pode levar até 24 horas.</p>
            </div>
            <button
                type="button"
                :disabled="props.checking || !props.canVerify"
                @click="emit('verify')"
            >
                {{ props.checking ? 'Verificando…' : (props.canVerify ? 'Verificar conexão' : 'Adicione o domínio primeiro') }}
            </button>
        </div>
    </section>
</template>

<style scoped>
.beta-dns-guide {
    overflow: hidden;
    border: 1px solid #dce5f0;
    border-radius: 20px;
    background: #f7f9fc;
}

.beta-dns-guide__heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 18px;
    border-bottom: 1px solid #e3e9f1;
    background: #fff;
}

.beta-dns-guide__heading p {
    color: var(--main-color);
    font-size: 10px;
    font-weight: 900;
    letter-spacing: 0.14em;
    text-transform: uppercase;
}

.beta-dns-guide__heading h3 {
    margin-top: 4px;
    color: #243a57;
    font-size: 14px;
    font-weight: 850;
}

.beta-dns-guide__heading span {
    display: block;
    margin-top: 5px;
    color: #7c8ca1;
    font-size: 11px;
    line-height: 1.5;
}

.beta-dns-guide__heading > small {
    flex: none;
    border-radius: 999px;
    background: color-mix(in srgb, var(--main-color) 9%, white);
    padding: 6px 9px;
    color: var(--main-color);
    font-size: 10px;
    font-weight: 850;
}

.beta-dns-guide__steps {
    display: grid;
    gap: 13px;
    padding: 18px 18px 0;
}

.beta-dns-guide__steps li,
.beta-dns-guide__finish {
    display: grid;
    grid-template-columns: 28px minmax(0, 1fr);
    align-items: start;
    gap: 10px;
}

.beta-dns-guide__steps li > span,
.beta-dns-guide__finish > span {
    display: grid;
    width: 28px;
    height: 28px;
    place-items: center;
    border-radius: 9px;
    background: var(--main-color);
    color: #fff;
    font-size: 10px;
    font-weight: 900;
}

.beta-dns-guide__steps strong,
.beta-dns-guide__finish strong {
    display: block;
    color: #2b415d;
    font-size: 14px;
    font-weight: 850;
}

.beta-dns-guide__steps p,
.beta-dns-guide__finish p {
    margin-top: 3px;
    color: #75869c;
    font-size: 12px;
    line-height: 1.6;
}

.beta-dns-guide__records {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    padding: 13px 18px 0 56px;
}

.beta-dns-guide__ttl {
    display: flex;
    gap: 8px;
    margin: 10px 18px 0 56px;
    border-radius: 10px;
    background: #edf2f7;
    padding: 9px 11px;
    color: #71839a;
    font-size: 10px;
}

.beta-dns-guide__ttl strong {
    color: #344d69;
}

.beta-dns-guide__providers {
    display: grid;
    gap: 8px;
    padding: 16px 18px;
}

.beta-dns-guide__providers details {
    overflow: hidden;
    border: 1px solid #e0e7f0;
    border-radius: 14px;
    background: #fff;
}

.beta-dns-guide__providers summary {
    display: flex;
    cursor: pointer;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 13px;
    list-style: none;
}

.beta-dns-guide__providers summary::-webkit-details-marker {
    display: none;
}

.beta-dns-guide__providers summary strong,
.beta-dns-guide__providers summary small {
    display: block;
}

.beta-dns-guide__providers summary strong {
    color: #314963;
    font-size: 12px;
    font-weight: 850;
}

.beta-dns-guide__providers summary small {
    margin-top: 3px;
    color: #8997aa;
    font-size: 10px;
}

.beta-dns-guide__providers summary svg {
    width: 17px;
    height: 17px;
    color: #71839b;
    transition: transform 160ms ease;
}

.beta-dns-guide__providers details[open] summary svg {
    transform: rotate(180deg);
}

.beta-dns-guide__provider-content {
    border-top: 1px solid #edf1f5;
    padding: 4px 15px 15px 34px;
}

.beta-dns-guide__provider-content ol {
    display: grid;
    gap: 7px;
    padding-top: 10px;
    color: #687b93;
    font-size: 12px;
    line-height: 1.65;
    list-style: decimal;
}

.beta-dns-guide__provider-content a {
    color: var(--main-color);
    font-weight: 850;
}

.beta-dns-guide__finish {
    grid-template-columns: 28px minmax(0, 1fr) auto;
    align-items: center;
    border-top: 1px solid #e1e8f0;
    background: #fff;
    padding: 16px 18px;
}

.beta-dns-guide__finish button {
    min-height: 36px;
    border-radius: var(--beta-button-radius);
    background: var(--main-color);
    padding: 0 13px;
    color: #fff;
    font-size: 10px;
    font-weight: 850;
}

.beta-dns-guide__finish button:disabled {
    cursor: wait;
    opacity: 0.55;
}

@media (max-width: 680px) {
    .beta-dns-guide__records {
        grid-template-columns: 1fr;
    }

    .beta-dns-guide__finish {
        grid-template-columns: 28px minmax(0, 1fr);
    }

    .beta-dns-guide__finish button {
        grid-column: 2;
        width: 100%;
    }
}

@media (max-width: 480px) {
    .beta-dns-guide__heading {
        flex-direction: column;
    }

    .beta-dns-guide__records,
    .beta-dns-guide__ttl {
        margin-left: 0;
        padding-right: 14px;
        padding-left: 14px;
    }

    .beta-dns-guide__ttl {
        margin-right: 14px;
    }
}
</style>
